import { Controller } from '@hotwired/stimulus'

// Manages a tabbed panel UI: shows one panel at a time and marks the active tab.
//
// Targets:
//   panel — a content section identified by data-tabs-panel-name="<name>"
//   tab   — an optional nav element identified by data-tabs-panel-name="<name>"
//
// Values:
//   activePanel (String, optional) — name of the panel to show on connect (defaults to first panel).
//   activeClass (String, default: 'active') — CSS class applied to the active tab target.
//   hiddenClass (String, default: 'display-none') — CSS class toggled on inactive panels.
//
// Usage (switch to a panel from any element):
//   data-action="click->tabs#show"
//   data-tabs-panel-param="edit"
export default class extends Controller {
  static targets = ['panel', 'tab']
  static values = {
    activePanel: String,
    activeClass: { type: String, default: 'active' },
    hiddenClass: { type: String, default: 'display-none' }
  }

  connect() {
    const panelName = this.hasActivePanelValue ? this.activePanelValue : this.defaultPanelName

    if (panelName) this.showPanel(panelName)
  }

  panelTargetConnected() {
    if (this.activePanelValue) this.showPanel(this.activePanelValue)
  }

  show(event) {
    event.preventDefault()

    const panel = event.params?.panel
    if (!panel) return

    this.activePanelValue = panel
    this.showPanel(panel)
  }

  showPanel(panelName) {
    const isKnown = this.panelTargets.some((panel) => panel.dataset.tabsPanelName === panelName)
    if (!isKnown) return

    this.panelTargets.forEach((panel) => {
      const isActive = panel.dataset.tabsPanelName === panelName

      panel.classList.toggle(this.hiddenClassValue, !isActive)
      panel.setAttribute('aria-hidden', String(!isActive))
    })

    this.tabTargets.forEach((tab) => {
      const isActive = tab.dataset.tabsPanelName === panelName

      tab.setAttribute('aria-selected', String(isActive))
      tab.classList.toggle(this.activeClassValue, isActive)

      const tabItem = tab.closest('[role="presentation"]')
      if (tabItem) tabItem.classList.toggle(this.activeClassValue, isActive)
    })
  }

  get defaultPanelName() {
    return this.panelTargets[0]?.dataset.tabsPanelName
  }
}
